/**
 * @name curpUtilities.service.ts
 * @version 1.0.0
 * @description Module for CURP operations and validations
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @santiagogonzalez - Initial migration from JS to TS
 */

import type { IPerson, ICurpVerificationResult, TStateCodes, TWordsForChange } from './curpUtilities.types'

// List of vowels and special characters
const _vowels = 'AEIOU/.-'

// List of consonants and special characters
const _consonants = 'BCDFGHJKLMNÑPQRSZTVWXYZ/.-'

// List of allowed states for positions 12 and 13
const _states: TStateCodes = {
	AS: 'AGUASCALIENTES',
	BC: 'BAJA CALIFORNIA',
	BS: 'BAJA CALIFORNIA SUR ',
	CC: 'CAMPECHE',
	CL: 'COAHUILA',
	CM: 'COLIMA',
	CS: 'CHIAPAS',
	CH: 'CHIHUAHUA',
	DF: 'DISTRITO FEDERAL',
	DG: 'DURANGO',
	GT: 'GUANAJUATO',
	GR: 'GUERRERO',
	HG: 'HIDALGO',
	JC: 'JALISCO',
	MC: 'MÉXICO',
	MN: 'MICHOACÁN',
	MS: 'MORELOS',
	NT: 'NAYARIT',
	NL: 'NUEVO LEÓN',
	OC: 'OAXACA',
	PL: 'PUEBLA',
	QT: 'QUERÉTARO',
	QR: 'QUINTANA ROO',
	SP: 'SAN LUIS POTOSÍ',
	SL: 'SINALOA',
	SR: 'SONORA',
	TC: 'TABASCO',
	TS: 'TAMAULIPAS',
	TL: 'TLAXCALA',
	VZ: 'VERACRUZ',
	YN: 'YUCATÁN',
	ZS: 'ZACATECAS',
	NE: 'Nacido en el Extranjero'
}

// List of words that must be replaced if they appear in the CURP
const _wordsForChange: TWordsForChange = {
	'BACA': 'BXCA',
	'LOCO': 'LXCO',
	'BAKA': 'BXKA',
	'LOKA': 'LXKA',
	'BUEI': 'BXEI',
	'LOKO': 'LXKO',
	'BUEY': 'BXEY',
	'MAME': 'MXME',
	'CACA': 'CXCA',
	'MAMO': 'MXMO',
	'CACO': 'CXCO',
	'MEAR': 'MXAR',
	'CAGA': 'CXGA',
	'MEAS': 'MXAS',
	'CAGO': 'CXGO',
	'MEON': 'MXON',
	'CAKA': 'CXKA',
	'MIAR': 'MXAR',
	'CAKO': 'CXKO',
	'MION': 'MXON',
	'COGE': 'CXGE',
	'MOCO': 'MXCO',
	'COGI': 'CXGI',
	'MOKO': 'MXKO',
	'COJA': 'CXJA',
	'MULA': 'MXLA',
	'COJE': 'CXJE',
	'MULO': 'MXLO',
	'COJI': 'CXJI',
	'NACA': 'NXCA',
	'COJO': 'CXJO',
	'NACO': 'NXCO',
	'COLA': 'CXLA',
	'PEDA': 'PXDA',
	'CULO': 'CXLO',
	'PEDO': 'PXDO',
	'FALO': 'FXLO',
	'PENE': 'PXNE',
	'FETO': 'FXTO',
	'PIPI': 'PXPI',
	'GETA': 'GXTA',
	'PITO': 'PXTO',
	'GUEI': 'GXEI',
	'POPO': 'PXPO',
	'GUEY': 'GXEY',
	'PUTA': 'PXTA',
	'JETA': 'JXTA',
	'PUTO': 'PXTO',
	'JOTO': 'JXTO',
	'QULO': 'QXLO',
	'KACA': 'KXCA',
	'RATA': 'RXTA',
	'KACO': 'KXCO',
	'ROBA': 'RXBA',
	'KAGA': 'KXGA',
	'ROBE': 'RXBE',
	'KAGO': 'KXGO',
	'ROBO': 'RXBO',
	'KAKA': 'KXKA',
	'RUIN': 'RXIN',
	'KAKO': 'KXKO',
	'SENO': 'SXNO',
	'KOGE': 'KXGE',
	'TETA': 'TXTA',
	'KOGI': 'KXGI',
	'VACA': 'VXCA',
	'KOJA': 'KXJA',
	'VAGA': 'VXGA',
	'KOJE': 'KXJE',
	'VAGO': 'VXGO',
	'KOJI': 'KXJI',
	'VAKA': 'VXKA',
	'KOJO': 'KXJO',
	'VUEI': 'VXEI',
	'KOLA': 'KXLA',
	'VUEY': 'VXEY',
	'KULO': 'KXLO',
	'WUEI': 'WXEI',
	'LILO': 'LXLO',
	'WUEY': 'WXEY',
	'LOCA': 'LXCA'
}

/**
 * Replaces accents and converts to uppercase
 * @description Function to replace accents and convert to uppercase
 * @param string - Word to replace accents
 * @returns String with accents replaced and converted to uppercase
 */
const _replaceTildes = (string: string): string => {
	const input = (string || '').toString()
	let word = input.toUpperCase()
	word = word.replace(/Á/gim, 'A')
	word = word.replace(/É/gim, 'E')
	word = word.replace(/Í/gim, 'I')
	word = word.replace(/Ó/gim, 'O')
	word = word.replace(/Ú/gim, 'U')
	word = word.replace(/Ü/gim, 'U')
	return word
}

/**
 * Finds the first match of a set of letters in a string
 * @description Function to find the first match of a set of letters in a string
 * @param string - Word where to search
 * @param letters - Set of letters or characters to search for
 * @returns First matching letter or 'X' if no match found
 */
const _searchLetter = (string: string, letters: string): string => {
	// Handle empty or null strings
	if (!string || string.trim() === '') {
		return 'X'
	}

	const words = _replaceTildes(string)
	const firstWord = words.split(' ')[0]

	// Handle case where firstWord is empty after processing
	if (!firstWord || firstWord.length === 0) {
		return 'X'
	}

	for (let i = 0; i < firstWord.length; i++) {
		if (i === 0) {
			continue
		}
		if (letters.indexOf(firstWord[i]) >= 0) {
			if (firstWord[i] === 'Ñ' || firstWord[i] === '/' || firstWord[i] === '-' || firstWord[i] === '.') {
				return 'X'
			}
			return firstWord[i]
		}
	}
	return 'X'
}

/**
 * Generates a CURP from a person object
 * @description Generates a CURP from a person object. Handles cases with or without second last name
 * @param person - Person data object (secondLastName is optional)
 * @returns CURP string, may be incomplete if not all data is provided
 */
export const makeCurp = (person: IPerson): string => {
	let year = '--'
	let month = '--'
	let day = '--'

	// Handle birthday if provided
	if (person.birthday) {
		const birthDate = new Date(person.birthday)
		// Validate that the date is valid
		if (!isNaN(birthDate.getTime())) {
			year = birthDate.getFullYear().toString().substring(2, 4)
			month = '0' + (birthDate.getMonth() + 1)
			month = month.substring(month.length - 2)
			day = '0' + birthDate.getDate()
			day = day.substring(day.length - 2)
		}
	}

	let genre = '-'
	// Handle genre if provided
	if (person.gender !== undefined && person.gender !== null) {
		if (person.gender === 251 || person.gender === 'H') {
			genre = 'H'
		} else if (person.gender === 250 || person.gender === 'M') {
			genre = 'M'
		}
	}

	// Ensure secondLastName is always a string and handle empty cases
	let secondLastName = person.secondLastName ? _replaceTildes(person.secondLastName) : ''

	let name = _replaceTildes(person.name)
	let firstLastName = _replaceTildes(person.firstLastName)

	const quitNamesAndSurnames = [
		'MA. DE LOS ',
		'MA. DE LA ',
		'MA ',
		'MA. DEL ',
		'MA. DE ',
		'MA. ',
		'DE LA ',
		'DE LOS ',
		'DE LAS ',
		'DA ',
		'DAS ',
		'DE ',
		'DEL ',
		'DER ',
		'DI ',
		'DIE ',
		'DD ',
		'EL ',
		'LA ',
		'LOS ',
		'LAS ',
		'LE ',
		'LES ',
		'MAC ',
		'MC ',
		'VAN ',
		'VON ',
		'Y '
	]

	for (const quitName of quitNamesAndSurnames) {
		if (name.startsWith(quitName)) {
			name = name.replace(quitName, '')
			break
		}
	}

	// Aplicar múltiples iteraciones para manejar apellidos compuestos
	let previousLength = firstLastName.length
	do {
		previousLength = firstLastName.length
		for (const quitName of quitNamesAndSurnames) {
			if (firstLastName.startsWith(quitName)) {
				firstLastName = firstLastName.replace(quitName, '')
				break
			}
		}
	} while (firstLastName.length < previousLength && firstLastName.length > 0)

	// Only process secondLastName if it's not empty
	if (secondLastName) {
		// Aplicar múltiples iteraciones para manejar apellidos compuestos como "Y de la Cruz"
		let previousLength = secondLastName.length
		do {
			previousLength = secondLastName.length
			for (const quitName of quitNamesAndSurnames) {
				if (secondLastName.startsWith(quitName)) {
					secondLastName = secondLastName.replace(quitName, '')
					break
				}
			}
		} while (secondLastName.length < previousLength && secondLastName.length > 0)
	}

	// Lista de prefijos que deben eliminarse de nombres
	const quitNames = [
		'MA. DE LOS ',
		'MA. DE LA',
		'MARIA DE LOS ',
		'MARIA DE LAS ',
		'MARIA DE LA ',
		'MARIA DEL ',
		'MARIA DE ',
		'MARIA ',
		'MA. DEL ',
		'MA. DE ',
		'MA. ',
		'MA ',
		'JOSE DE ',
		'JOSE ',
		'J ',
		'J. ',
		'M. '
	]

	for (const quitName of quitNames) {
		if (name.startsWith(quitName)) {
			name = name.replace(quitName, '')
			break
		}
	}

	let expectedCurp =
		firstLastName[0] +
		_searchLetter(firstLastName, _vowels) +
		(secondLastName && secondLastName.length > 0 ? secondLastName[0] : 'X') +
		name[0] +
		year +
		month +
		day +
		genre +
		(person.state || '--') +
		_searchLetter(firstLastName, _consonants) +
		_searchLetter(secondLastName, _consonants) +
		_searchLetter(name, _consonants) +
		(person.codeVerified || '')

	const first4 = expectedCurp.substring(0, 4)
	if (_wordsForChange[first4]) {
		expectedCurp = _wordsForChange[first4] + expectedCurp.substring(4, expectedCurp.length)
	}

	return expectedCurp
}

/**
 * Aplica el catálogo de palabras inconvenientes a las primeras 4 letras.
 * Si el prefijo no está en el catálogo, se deja igual (p. ej. RXBA ya filtrado).
 */
const _normalizeInconvenientPrefix = (prefix: string): string => {
	const upper = prefix.toUpperCase()
	return _wordsForChange[upper] || upper
}

/**
 * Verifies how similar the CURP is and if possible the existence of error
 * @description Verifies CURP format and compares with expected CURP from person data.
 * Handles cases with or without second last name (secondLastName is optional)
 * @param curp - 18-digit string for CURP validation
 * @param person - Person data to calculate expected CURP (secondLastName is optional)
 * @returns Verification result object
 */
export const verifyCurp = (curp: string, person: IPerson): ICurpVerificationResult => {
	curp = curp.trim().toUpperCase()

	if (curp === 'EXTRANJERO') {
		return {
			code: 1,
			message: 'Curp de extranjero'
		}
	}

	if (curp.length !== 18) {
		return {
			code: -1,
			message: 'El curp debe ser de 18 caracteres o la palabra "EXTRANJERO"'
		}
	}

	const stateOfCurp = curp.substring(11, 13)
	if (!_states[stateOfCurp]) {
		return {
			code: -1,
			message: `El estado ${stateOfCurp} no es correcto`
		}
	}

	if (!/^([A-Z]{4})\d{2}[0-1]\d[0-3]\d([H|M])([A-Z]{5})(.)\d/im.test(curp)) {
		return {
			code: -1,
			message: 'El curp no cumple con el formato'
		}
	}

	const expectedCurp = makeCurp(person)
	const actualFirst4 = curp.substring(0, 4)
	const expectedFirst4 = expectedCurp.substring(0, 4)
	// RENAPO sustituye la 2.ª letra por X en palabras inconvenientes, pero hay CURP
	// oficiales emitidas sin ese cambio (p. ej. ROBA vs RXBA). Se aceptan ambas.
	let valid = _normalizeInconvenientPrefix(actualFirst4) === _normalizeInconvenientPrefix(expectedFirst4)
	console.log(valid)
	for (let i = 4; i < expectedCurp.length; i++) {
		if (i !== 11 && i !== 12) {
			if (curp[i] !== expectedCurp[i] && expectedCurp[i] !== '-') {
				valid = false
			}
		}
	}

	if (valid) {
		return {
			code: 1,
			message: 'Formato correcto'
		}
	} else {
		return {
			code: 2,
			message: 'Parece que el curp no corresponde con la persona recibida',
			expectedCurp
		}
	}
}
